import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { User } from '@angular/fire/auth';
import { Subscription, filter, take } from 'rxjs';
import {
  AVAILABILITY_LABEL,
  CollaboratorAvailability,
  CollaboratorRole,
} from '@app/core/models';
import { AuthService } from '@app/core/services/auth.service';
import { SnackbarService } from '@app/core/services/snackbar.service';

import {
  PartnerFieldDefinition,
  PartnerLink,
  PartnerProjectEntry,
  PartnerRoleConfig,
} from '../../models/partner.models';
import { PartnerRoleConfigService } from '../../services/partner-role-config';
import { PartnerApplicationService } from '../../services/partner-application';

/** FormArray validator — used on notableProjects to require at least one
 *  entry, since that's the section doing the most work to make a profile
 *  feel earned rather than self-described. */
function minArrayLength(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return Array.isArray(value) && value.length >= min ? null : { minArrayLength: { min } };
  };
}

const ALLOWED_RESUME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_RESUME_BYTES = 5 * 1024 * 1024;

/**
 * The Execution Partner application form. Renders a fixed shared-field
 * section (name, email, headline, bio, availability, notable projects,
 * links, resume) plus a config-driven section for whichever role is
 * currently selected (see PartnerRoleConfigService) — switching role
 * rebuilds that section from scratch, so answers don't carry across roles.
 *
 * Requires a logged-in user to actually submit (uid is required on
 * PartnerApplication) — the form itself is viewable and fillable while
 * logged out, but submit() redirects to /login rather than losing the
 * user's place mid-form.
 */
@Component({
  selector: 'app-partner-apply',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, MatIconModule],
  templateUrl: './partner-apply.html',
  styleUrls: ['./partner-apply.css'],
})
export class PartnerApply implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snack = inject(SnackbarService);
  private readonly roleConfig = inject(PartnerRoleConfigService);
  private readonly applications = inject(PartnerApplicationService);
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  private sub = new Subscription();

  user: User | null = null;
  userChecked = false;
  submitting = false;

  /** True while checking whether this user already has a blocking
   *  application (pending/approved) — the form is hidden behind this
   *  until resolved, so there's no flash of a blank form for someone
   *  who's about to get redirected to their status page instead. */
  checkingExisting = true;

  readonly roles: PartnerRoleConfig[] = this.roleConfig.listRoles();
  readonly availabilityOptions: { value: CollaboratorAvailability; label: string }[] =
    Object.entries(AVAILABILITY_LABEL).map(([value, label]) => ({
      value: value as CollaboratorAvailability,
      label,
    }));

  selectedRoleFields: PartnerFieldDefinition[] = [];
  /** Per-field "currently typed, not yet added" text for freeform chip
   *  fields — keyed by field id, kept outside the reactive form since it's
   *  transient UI state, not a submittable value. */
  chipDraft: Record<string, string> = {};

  resumeFile: File | null = null;
  resumeFileName = '';
  resumeTouched = false;

  readonly form: FormGroup = this.fb.group({
    fullName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    location: [''],
    role: ['', Validators.required],
    headline: ['', Validators.required],
    bio: ['', [Validators.required, Validators.minLength(40)]],
    yearsExperience: [null as number | null, [Validators.required, Validators.min(0), Validators.max(60)]],
    availability: ['', Validators.required],
    links: this.fb.array([]),
    notableProjects: this.fb.array([], [minArrayLength(1)]),
    roleFields: this.fb.group({}),
  });

  ngOnInit(): void {
    if (!this.isBrowser) return;

    // Rebuild the role-specific section any time `role` changes — must be
    // subscribed BEFORE the query-param prefill below, so a pre-selected
    // role (from the landing page's role cards) still triggers it.
    this.sub.add(
      this.form.get('role')!.valueChanges.subscribe((role: CollaboratorRole) => {
        this.onRoleChange(role);
      }),
    );

    this.sub.add(
      this.auth.user$
        .pipe(
          filter((u): u is User | null => u !== undefined),
          take(1),
        )
        .subscribe(async (user) => {
          this.user = user;
          this.userChecked = true;

          if (!user) {
            this.checkingExisting = false;
            return;
          }

          this.form.patchValue({ email: user.email ?? '' });

          // The service already blocks a duplicate submit for pending/
          // approved applications — this is the same rule enforced at the
          // page level, so someone who already applied sees their status
          // instead of a confusing blank form they can't actually submit.
          try {
            const latest = await this.applications.getMyLatestApplication(user.uid);
            if (latest && (latest.status === 'pending' || latest.status === 'approved')) {
              this.router.navigate(['../applied'], { relativeTo: this.route });
              return;
            }
          } catch {
            // If the check itself fails, fall through to showing the form —
            // worst case the submit-time check in the service catches a
            // true duplicate; we shouldn't block a legitimate applicant
            // over a transient read error.
          }
          this.checkingExisting = false;
        }),
    );

    const roleParam = this.route.snapshot.queryParamMap.get('role');
    if (roleParam && this.roleConfig.getRoleConfig(roleParam as CollaboratorRole)) {
      this.form.get('role')!.setValue(roleParam);
    }

    // Start with one visible (empty) project row rather than a blank list —
    // makes the "at least one" requirement obvious instead of implicit.
    this.addProject();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /* ── Role-specific fields ─────────────────────────────────────────── */

  private onRoleChange(role: CollaboratorRole): void {
    const fields = this.roleConfig.getFields(role);
    this.selectedRoleFields = fields;

    const group: Record<string, FormControl> = {};
    for (const f of fields) {
      if (f.type === 'chips' || f.type === 'multiselect') {
        group[f.id] = this.fb.control(
          [] as string[],
          f.required ? [minArrayLength(1)] : [],
        );
      } else if (f.type === 'number') {
        group[f.id] = this.fb.control(null, f.required ? [Validators.required] : []);
      } else {
        group[f.id] = this.fb.control('', f.required ? [Validators.required] : []);
      }
    }
    this.form.setControl('roleFields', this.fb.group(group));
    this.chipDraft = {};
  }

  roleFieldControl(fieldId: string): FormControl {
    return (this.form.get('roleFields') as FormGroup).get(fieldId) as FormControl;
  }

  addChip(fieldId: string): void {
    const draft = (this.chipDraft[fieldId] ?? '').trim();
    if (!draft) return;
    const control = this.roleFieldControl(fieldId);
    const current: string[] = control.value ?? [];
    if (!current.includes(draft)) {
      control.setValue([...current, draft]);
    }
    this.chipDraft[fieldId] = '';
  }

  removeChip(fieldId: string, index: number): void {
    const control = this.roleFieldControl(fieldId);
    const current: string[] = control.value ?? [];
    control.setValue(current.filter((_, i) => i !== index));
  }

  toggleMultiselectOption(fieldId: string, value: string): void {
    const control = this.roleFieldControl(fieldId);
    const current: string[] = control.value ?? [];
    control.setValue(
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  }

  /* ── Links ─────────────────────────────────────────────────────────── */

  get linksArray(): FormArray {
    return this.form.get('links') as FormArray;
  }

  addLink(): void {
    this.linksArray.push(
      this.fb.group({
        label: ['', Validators.required],
        url: ['', Validators.required],
      }),
    );
  }

  removeLink(index: number): void {
    this.linksArray.removeAt(index);
  }

  /* ── Notable projects ─────────────────────────────────────────────── */

  get projectsArray(): FormArray {
    return this.form.get('notableProjects') as FormArray;
  }

  addProject(): void {
    this.projectsArray.push(
      this.fb.group({
        name: ['', Validators.required],
        url: [''],
        description: ['', Validators.required],
      }),
    );
  }

  removeProject(index: number): void {
    // Always leave at least one row visible — the array-level validator
    // requires it anyway, so an empty list would just show as invalid.
    if (this.projectsArray.length <= 1) return;
    this.projectsArray.removeAt(index);
  }

  /* ── Resume upload ─────────────────────────────────────────────────── */

  onResumeSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.resumeTouched = true;

    if (!file) {
      this.resumeFile = null;
      this.resumeFileName = '';
      return;
    }
    if (!ALLOWED_RESUME_TYPES.includes(file.type)) {
      this.snack.showError('Please upload a PDF or Word document (.pdf, .doc, .docx).');
      input.value = '';
      this.resumeFile = null;
      this.resumeFileName = '';
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      this.snack.showError('Resume must be under 5MB.');
      input.value = '';
      this.resumeFile = null;
      this.resumeFileName = '';
      return;
    }
    this.resumeFile = file;
    this.resumeFileName = file.name;
  }

  /* ── Submit ────────────────────────────────────────────────────────── */

  fieldInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  async submit(): Promise<void> {
    if (!this.user) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
      return;
    }

    this.resumeTouched = true;
    this.form.markAllAsTouched();

    if (this.form.invalid || !this.resumeFile) {
      this.snack.showError('Please complete all required fields before submitting.');
      return;
    }

    this.submitting = true;
    try {
      const raw = this.form.getRawValue();
      await this.applications.submitApplication({
        uid: this.user.uid,
        email: raw.email,
        fullName: raw.fullName,
        phone: raw.phone || undefined,
        location: raw.location || undefined,
        role: raw.role,
        headline: raw.headline,
        bio: raw.bio,
        yearsExperience: raw.yearsExperience,
        availability: raw.availability,
        resumeFile: this.resumeFile,
        links: raw.links as PartnerLink[],
        notableProjects: (raw.notableProjects as Array<{ name: string; url: string; description: string }>).map(
          (p) => ({
            id: this.newId(),
            name: p.name,
            url: p.url || undefined,
            description: p.description,
          }),
        ) as PartnerProjectEntry[],
        roleFieldValues: raw.roleFields,
      });

      this.router.navigate(['../applied'], { relativeTo: this.route });
    } catch (err: any) {
      this.snack.showError(err?.message ?? 'Could not submit your application. Please try again.');
    } finally {
      this.submitting = false;
    }
  }

  private newId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}